import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environments/environment';

const TITLE_SEPARATOR = ' - ';

/**
 * Routes opt in by setting `data: { titleKey: 'queue_live.nav_title' }` (i18n key).
 * Components that need a fully resolved value (e.g. counter name) call `setRawTitle()`.
 * Routes without `titleKey` are left untouched so per-component setTitle() calls keep working.
 */
@Injectable({ providedIn: 'root' })
export class CalensoTitleStrategy extends TitleStrategy {

  private readonly titleService = inject(Title);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private currentTitleKey: string | null = null;

  constructor() {
    super();
    this.translateService.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.currentTitleKey) {
          this.applyKey(this.currentTitleKey);
        }
      });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const titleKey = this.findTitleKey(snapshot.root);
    if (titleKey) {
      this.currentTitleKey = titleKey;
      this.applyKey(titleKey);
    } else {
      this.currentTitleKey = null;
    }
  }

  setRawTitle(text: string): void {
    this.currentTitleKey = null;
    this.titleService.setTitle(`${environment.brandName}${TITLE_SEPARATOR}${text}`);
  }

  private applyKey(key: string): void {
    this.translateService.get(key).subscribe(translated => {
      this.titleService.setTitle(`${environment.brandName}${TITLE_SEPARATOR}${translated}`);
    });
  }

  private findTitleKey(snapshot: ActivatedRouteSnapshot): string | null {
    let current: ActivatedRouteSnapshot | null = snapshot;
    let key: string | null = null;
    while (current) {
      const candidate = current.data?.['titleKey'];
      if (typeof candidate === 'string' && candidate.length > 0) {
        key = candidate;
      }
      current = current.firstChild;
    }
    return key;
  }
}
